import { action } from "@ember/object"
import Route from "@ember/routing/route"
import { scheduleOnce } from "@ember/runloop"
import { inject as service } from "@ember/service"
import { omit } from "lodash-es"
import isMobile from "../../lib/is-mobile"

import type EmberStore from "cc-frontend/services/ember-store"
import type SessionStore from "cc-frontend/services/session-store"

export default class PlannerRoute extends Route {
  @service("ember-store") declare store: EmberStore
  @service declare sessionStore: SessionStore
  @service router

  beforeModel() {
    if (isMobile().phone) {
      this.transitionTo("planbookv5.planner.day")
    }
  }

  @action
  didTransition() {
    // do not load anything saved if this is coming from a shared link
    if (this.router.currentRoute?.queryParams.hideNav === true) return true

    // @ts-ignore Not sure why it doesn't know there's not a context
    let planbookId = this.context.planbook?.id
    let storedLastView = this.sessionStore.fetchObject<{
      route: string
      queryParams?: Record<string, string>
    }>(`planbook-${planbookId}-lastView`) || {
      route: "planbookv5.planner.week",
    }

    // Currently, we're omitting the date so that people aren't taken to a previous week
    // We might make this more sophisticated in the future or remove other settings
    // like filtering classes.
    let storedQueryParams = omit(storedLastView.queryParams, [
      "date",
      "stepName",
      "hideNav",
      "openedModals",
    ])

    let queryParams = parseQueryParams(storedQueryParams)

    scheduleOnce("afterRender", this, () => {
      if (this.router.currentRouteName === "planbookv5.planner.index") {
        if (queryParams) {
          this.replaceWith(storedLastView.route, { queryParams: { ...queryParams } })
        } else {
          this.replaceWith(storedLastView.route)
        }
      }
    })
    return
  }
}

const BOOLEAN_PARAMS = ["classesLinedUp", "daysAsRows", "lessonsCollapsed"]
const STRING_PARAMS = ["focusedItemId", "selectedCourseId"]

// `transition.to.queryParams` — where these were saved from — holds the *serialized*
// values Ember puts in the URL, not the deserialized ones on the controller. So booleans
// arrive as "true"/"false" and arrays as JSON strings. Turn them back into real values,
// returning null if anything doesn't look like what we expect.
// If we extend this in the future, consider using Zod instead of custom code.
function parseQueryParams(
  storedQueryParams: Record<string, string>,
): Record<string, unknown> | null {
  let queryParams: Record<string, unknown> = {}

  for (const key of BOOLEAN_PARAMS) {
    const value = storedQueryParams[key]
    if (value === undefined) continue
    if (value !== "true" && value !== "false") return null
    queryParams[key] = value === "true"
  }

  for (const key of STRING_PARAMS) {
    const value = storedQueryParams[key]
    // We need to map undefined (or null) to no value.
    if (value === undefined || value === null) continue
    if (typeof value !== "string") return null
    queryParams[key] = value
  }

  const hiddenCourseIds = storedQueryParams.hiddenCourseIds
  if (hiddenCourseIds !== undefined) {
    if (typeof hiddenCourseIds !== "string") return null

    let parsed: unknown
    // We try/catch this b/c parsing JSON always feels a bit dangerous.
    // and we don't want this to fail on us at all.
    try {
      parsed = JSON.parse(hiddenCourseIds)
    } catch (e) {
      console.log("error parsing hiddenCourseIds", e)
      return null
    }

    if (!(parsed instanceof Array && parsed.every((value) => typeof value === "string"))) {
      return null
    }
    queryParams.hiddenCourseIds = parsed
  }

  return queryParams
}
